import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SiteContentProvider } from './context/SiteContent';
import { ThemeProvider } from './context/ThemeContext';
import StoreEditorSync from './components/StoreEditorSync';
import TapasStickyNav from './components/TapasStickyNav';
import SiteFooter from './components/SiteFooter';
import InstallPrompt from './components/InstallPrompt';
import './App.css';

// Auth flows get a dedicated split-screen layout; stacking the
// sticky navbar on top would compete with the focused form. Add any
// new auth routes to this set to hide the nav there.
// /links is a link-in-bio page — it is opened from an Instagram bio and has to
// be the whole screen, so the sticky nav and the full site footer stay off it.
const HIDE_NAV_ROUTES = new Set(['/links']);

function GlobalHeader() {
  const { pathname } = useLocation();
  if (HIDE_NAV_ROUTES.has(pathname)) return null;
  return <TapasStickyNav />;
}

function GlobalFooter() {
  // SiteFooter owns the footer for the entire site. The landing tree
  // no longer carries its own footer node, so nothing else renders
  // below; SiteFooter always takes over.
  const { pathname } = useLocation();
  if (HIDE_NAV_ROUTES.has(pathname)) return null;
  return <SiteFooter />;
}

// Recover from ChunkLoadError by forcing one hard reload. Prevents a
// stale browser tab from being stuck after a dev rebuild or deploy.
function lazyWithRetry(importFn) {
  return React.lazy(() =>
    importFn()
      .then((mod) => {
        try { sessionStorage.removeItem('tapas_store_chunk_reload'); } catch {}
        return mod;
      })
      .catch((err) => {
        const isChunkError = err?.name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(err?.message || '');
        if (isChunkError) {
          try {
            if (!sessionStorage.getItem('tapas_store_chunk_reload')) {
              sessionStorage.setItem('tapas_store_chunk_reload', String(Date.now()));
              window.location.reload();
              return new Promise(() => {});
            }
          } catch {}
        }
        throw err;
      })
  );
}

const Home            = lazyWithRetry(() => import('./pages/Home'));
const Offers          = lazyWithRetry(() => import('./pages/Offers'));
const About           = lazyWithRetry(() => import('./pages/About'));
const CustomerLogin   = lazyWithRetry(() => import('./pages/CustomerLogin'));
const Profile         = lazyWithRetry(() => import('./pages/Profile'));
const Blog            = lazyWithRetry(() => import('./pages/Blog'));
const BlogPost        = lazyWithRetry(() => import('./pages/BlogPost'));
const CustomPage      = lazyWithRetry(() => import('./pages/CustomPage'));
// Shop temporarily shows the "Coming Soon" page (see /shop route below).
// const Shop            = lazyWithRetry(() => import('./pages/Shop'));
const Contact         = lazyWithRetry(() => import('./pages/Contact'));
const Events          = lazyWithRetry(() => import('./pages/Events'));
const BookCatalog     = lazyWithRetry(() => import('./pages/BookCatalog'));
const EventDetail     = lazyWithRetry(() => import('./pages/EventDetail'));
const SignIn          = lazyWithRetry(() => import('./pages/SignIn'));
const SignUp          = lazyWithRetry(() => import('./pages/SignUp'));
const ForgotPassword  = lazyWithRetry(() => import('./pages/ForgotPassword'));
const Welcome         = lazyWithRetry(() => import('./pages/Welcome'));
const CodeOfTheRoom   = lazyWithRetry(() => import('./pages/CodeOfTheRoom'));
const Privacy         = lazyWithRetry(() => import('./pages/Privacy'));
const Links           = lazyWithRetry(() => import('./pages/Links'));
const ComingSoon      = lazyWithRetry(() => import('./pages/ComingSoon'));

// ---------------------------------------------------------------------
// Backward-compat shim: existing pages (BookDetail, CustomerLogin, the
// retired MemberDashboard) import `useApp` from this file. Instead of
// touching every page, expose a thin shim that pulls the same fields
// from AuthContext.
// ---------------------------------------------------------------------
export function useApp() {
  const auth = useAuth();
  return {
    member: auth.member,
    wishlistCount: auth.wishlistCount,
    setMember: auth.setMember,
    setWishlistCount: auth.setWishlistCount,
    refresh: auth.refresh,
  };
}

function PageLoader() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', flexDirection:'column', gap:'16px' }}>
      <div style={{ fontSize:'48px', animation:'bookSpin 1s ease-in-out infinite' }}>📚</div>
      <p style={{ color:'#8B6914', fontFamily:'Lato, sans-serif' }}>Loading...</p>
      <style>{`@keyframes bookSpin { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }`}</style>
    </div>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  return (
    <>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap" />
      <style>{`
        :root {
          --tapas-heading-font: 'Poppins', system-ui, sans-serif;
          --tapas-body-font: 'Poppins', system-ui, sans-serif;
        }
        body, html { font-family: 'Poppins', system-ui, sans-serif; }
      `}</style>
      <StoreEditorSync />
      <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', background: pathname === '/' ? '#caf27e' : '#F6F8F7', color:'var(--text)', paddingTop: HIDE_NAV_ROUTES.has(pathname) ? 0 : 86, transition:'background 200ms, color 200ms' }}>
        <GlobalHeader />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/"              element={<Home />} />
            <Route path="/books/:id"     element={<Navigate to="/" replace />} />
            <Route path="/offers"        element={<Offers />} />
            <Route path="/about"         element={<About />} />
            <Route path="/blog"          element={<Blog />} />
            <Route path="/blog/:slug"    element={<BlogPost />} />
            <Route path="/login"         element={<CustomerLogin />} />
            <Route path="/profile"       element={<Profile />} />
            {/* Backward-compat: /member now redirects to /profile */}
            <Route path="/member"        element={<Navigate to="/profile" replace />} />
            <Route path="/cart"          element={<Navigate to="/" replace />} />
            <Route path="/checkout"      element={<Navigate to="/" replace />} />
            <Route path="/order/:id"     element={<Navigate to="/" replace />} />
            <Route path="/order/:id/track" element={<Navigate to="/" replace />} />
            <Route path="/search"        element={<Navigate to="/" replace />} />
            <Route path="/shop"          element={<Navigate to="/" replace />} />
            <Route path="/contact"       element={<Contact />} />
            <Route path="/library"       element={<BookCatalog />} />
            <Route path="/events"        element={<Events />} />
            <Route path="/events/:slug"  element={<EventDetail />} />
            <Route path="/sign-in"       element={<SignIn />} />
            <Route path="/sign-up"       element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/welcome"       element={<Welcome />} />
            <Route path="/code-of-the-room" element={<CodeOfTheRoom />} />
            <Route path="/links"         element={<Links />} />
            <Route path="/privacy"       element={<Privacy />} />
            <Route path="/orders"        element={<Navigate to="/" replace />} />
            <Route path="/wishlist"      element={<Navigate to="/" replace />} />
            <Route path="/faq"           element={<ComingSoon />} />
            <Route path="/faqs"          element={<ComingSoon />} />
            <Route path="/terms"         element={<ComingSoon />} />
            <Route path="/products"      element={<Navigate to="/" replace />} />
            <Route path="/track-order"   element={<Navigate to="/" replace />} />
            {/* Catch-all: resolve against custom pages in SiteContent,
                or render a 404 card. Must be last. */}
            <Route path="*"              element={<CustomPage />} />
          </Routes>
        </Suspense>
        <GlobalFooter />
        <InstallPrompt />
      </div>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <SiteContentProvider>
        <AuthProvider>
          <CartProvider>
            <AppShell />
          </CartProvider>
        </AuthProvider>
      </SiteContentProvider>
    </ThemeProvider>
  );
}
